import { useEffect, useState, useCallback, useRef } from "react";
import { MdPeople, MdClose, MdRefresh, MdWifi, MdWifiOff } from "react-icons/md";
import api from "../../services/api";
import socket from "../../services/socket";
import { formatDistanceToNow } from "date-fns";
import "./RightSidebar.css";

const API_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_URL || window.location.origin
    : "http://localhost:6000";

const formatLastSeen = (d) => {
  if (!d) return "Last seen: —";
  try {
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return "Last seen: —";
    return `Last seen ${formatDistanceToNow(date, { addSuffix: true })}`;
  } catch {
    return "Last seen: —";
  }
};

const sameId = (a, b) => a && b && String(a) === String(b);

const RightSidebar = () => {
  const [open, setOpen]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const [users, setUsers]       = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [socketOk, setSocketOk] = useState(socket.connected);
  const intervalRef             = useRef(null);

  // ── Fetch users list from server ──────────────────────────────────────────
  const fetchUsers = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const { data } = await api.get("/users/list");
      setUsers(data.data || []);
      setLastSync(new Date());
    } catch (err) {
      console.error("RightSidebar: failed to load users", err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Re-fetch whenever the sidebar is opened to always show fresh data
  useEffect(() => {
    if (open) fetchUsers(true);
  }, [open, fetchUsers]);

  // Periodic silent re-sync every 20 s
  useEffect(() => {
    intervalRef.current = setInterval(() => fetchUsers(true), 20_000);
    return () => clearInterval(intervalRef.current);
  }, [fetchUsers]);

  // Re-fetch when the browser tab becomes visible again
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchUsers(true);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchUsers]);

  // ── Socket: real-time presence updates ───────────────────────────────────
  useEffect(() => {
    const handlePresence = (payload = {}) => {
      const { userId, isOnline, lastSeen } = payload;
      if (!userId) return;
      setUsers((prev) => {
        const idx = prev.findIndex((u) => sameId(u._id, userId));
        if (idx === -1) {
          fetchUsers(true);
          return prev;
        }
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          isOnline: !!isOnline,
          // Only update lastSeen when going offline (server never sends lastSeen on connect now)
          ...(lastSeen !== undefined ? { lastSeen } : {}),
        };
        return next;
      });
    };

    const handleConnect    = () => { setSocketOk(true); };
    const handleDisconnect = () => setSocketOk(false);

    // Server finished boot + cleanup → wait for presence:update events then re-fetch
    const handleServerReady = () => {
      // Delay slightly so all joinUserRoom events are processed first
      setTimeout(() => fetchUsers(true), 1500);
    };

    socket.on("presence:update", handlePresence);
    socket.on("connect",         handleConnect);
    socket.on("disconnect",      handleDisconnect);
    socket.on("server:ready",    handleServerReady);

    setSocketOk(socket.connected);

    return () => {
      socket.off("presence:update", handlePresence);
      socket.off("connect",         handleConnect);
      socket.off("disconnect",      handleDisconnect);
      socket.off("server:ready",    handleServerReady);
    };
  }, [fetchUsers]);

  // ── Sort: online first, then alphabetical ────────────────────────────────
  const sortedUsers = [...users].sort((a, b) => {
    if (a.isOnline && !b.isOnline) return -1;
    if (!a.isOnline && b.isOnline) return 1;
    return (a.name || "").localeCompare(b.name || "");
  });

  const onlineCount  = users.filter((u) => u.isOnline).length;
  const offlineCount = users.filter((u) => !u.isOnline).length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Floating toggle button */}
      <button
        type="button"
        className="rightbar-toggle-btn"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="Toggle staff sidebar"
      >
        <MdPeople />
        {onlineCount > 0 && (
          <span className="rightbar-toggle-badge">{onlineCount}</span>
        )}
      </button>

      <aside className={`rightbar ${open ? "rightbar-open" : ""}`}>
        {/* Header */}
        <div className="rightbar-header">
          <div>
            <h3 className="rightbar-title">Staff Presence</h3>
            <p className="rightbar-subtitle">
              <span className="rightbar-stat online">{onlineCount} online</span>
              <span className="rightbar-stat-sep">·</span>
              <span className="rightbar-stat offline">{offlineCount} offline</span>
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {/* Socket connection indicator */}
            <span
              className={`rightbar-socket-dot ${socketOk ? "connected" : "disconnected"}`}
              title={socketOk ? "Real-time connected" : "Real-time disconnected — manual refresh only"}
            >
              {socketOk
                ? <MdWifi size={14} />
                : <MdWifiOff size={14} />
              }
            </span>

            <button
              type="button"
              className="btn-icon rightbar-refresh-btn"
              onClick={() => fetchUsers()}
              aria-label="Refresh presence"
              title={lastSync
                ? `Last synced ${formatDistanceToNow(lastSync, { addSuffix: true })}`
                : "Refresh"}
            >
              <MdRefresh size={16} />
            </button>

            <button
              type="button"
              className="btn-icon rightbar-close-btn"
              onClick={() => setOpen(false)}
              aria-label="Close staff sidebar"
            >
              <MdClose size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="rightbar-body">
          {loading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : sortedUsers.length === 0 ? (
            <div className="empty-state">
              <MdPeople size={40} className="empty-icon" />
              <h3>No staff found</h3>
              <p>Staff members will appear here once created.</p>
            </div>
          ) : (
            <ul className="rightbar-list">
              {sortedUsers.map((u) => (
                <li key={u._id} className={`rightbar-item ${u.isOnline ? "rightbar-item--online" : ""}`}>
                  <div className="rightbar-avatar-wrapper">
                    <div className="rightbar-avatar">
                      {u.avatar ? (
                        <img
                          src={u.avatar.startsWith("http") ? u.avatar : `${API_URL}${u.avatar}`}
                          alt={u.name}
                        />
                      ) : (
                        (u.name || "?").split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
                      )}
                    </div>
                    <span className={`rightbar-status-dot ${u.isOnline ? "online" : "offline"}`} />
                  </div>

                  <div className="rightbar-item-main">
                    <div className="rightbar-item-name-row">
                      <span className="rightbar-item-name">{u.name}</span>
                      <span className={`rightbar-status-pill ${u.isOnline ? "online" : "offline"}`}>
                        {u.isOnline ? "Online" : "Offline"}
                      </span>
                    </div>
                    <div className="rightbar-item-email">{u.email}</div>
                    {u.designation && (
                      <div className="rightbar-item-designation">{u.designation}</div>
                    )}
                    <div className="rightbar-item-lastseen">
                      {u.isOnline
                        ? <span className="rightbar-lastseen-online">● Active now</span>
                        : formatLastSeen(u.lastSeen)
                      }
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </>
  );
};

export default RightSidebar;
